/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { getS3BucketPlans } from '@/api/other/getS3BucketPlans'
import { postBucket } from '@/api/other/postBucket'
import { IconDownarrow } from '@/res/icons'
import { NAMESPACE_S3_BUCKET } from '@/res/namespaces'
import { ROUTE_LOGIN, ROUTE_PAYMENT, ROUTE_SETTINGS } from '@/res/routes'
import { numberWithSpaces } from '@/utils/numberWithSpaces'

interface FormValues {
  bucket_quota: number
  name: string
  bucket_name: string
  token: string
}

const Cost = () => {
  const { t } = useTranslation(NAMESPACE_S3_BUCKET)
  const { data } = useSession()
  const router = useRouter()
  const [price, setPrice] = useState('')
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      bucket_quota: 1,
      name: t('GB'),
      bucket_name: '',
    },
    mode: 'onChange',
  })
  useEffect(() => {
    const getData = async () => {
      const res = await getS3BucketPlans()
      setPrice(res[0].price)
    }
    getData()
  }, [])

  const onSubmit = async ({ bucket_quota, bucket_name }: FormValues) => {
    try {
      const token = data?.user.accessToken
      if (!data?.user) {
        router.push({
          pathname: ROUTE_LOGIN,
          query: {
            from: router.asPath,
          },
        })
      } else if (!data?.user.phone_number) {
        router.push({
          pathname: ROUTE_SETTINGS,
          query: {
            show_info: true,
          },
        })
      } else {
        const formData = {
          bucket_name: bucket_name,
          bucket_quota: bucket_quota,
        }
        const res = await postBucket(formData as any, token as any)
        router.push(`${ROUTE_PAYMENT}/${res.uid}`)
      }
    } catch (err: any) {
      const errors = err.response?.data?.errors || {}
      Object.keys(errors).forEach((fieldErr) => {
        setError(fieldErr as keyof FormValues, {
          message: errors[fieldErr],
        })
      })
    }
  }

  const [value, setValue] = useState(1)

  const blockInvalidChar = (e: any) =>
    ['e', 'E', '+', '-', ',', '.'].includes(e.key) && e.preventDefault()

  const validations = [
    {
      text: t('validation1'),
    },
    {
      text: t('validation2'),
    },
    {
      text: t('validation3'),
    },
    {
      text: t('validation4'),
    },
    {
      text: t('validation5'),
    },
    {
      text: t('validation6'),
    },
    {
      text: t('validation7'),
    },
  ]

  const [isOpen, setIsOpen] = useState(false)
  const toggleDropdown = () => setIsOpen((prevState) => !prevState)
  const handleOptionClick = () => {
    setIsOpen(false)
  }
  return (
    <div id="cost" className="container pb-16 lg:pb-20 cost ">
      <div className="md:pl-10 xl:py-10 card py-5 px-3 bg-[#FFFFFF] rounded-[16px] pl-5 pb-14 ">
        <h5 className="md:text-2xl text-xl font-semibold text-[#242F3C]}">
          {t('calculate_cost')}
        </h5>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col justify-between py-6 xl:flex-row"
        >
          <div className="flex flex-col pr-10 xl:border-r-2">
            <h6 className="xl:pb-[17px] xl:pl-6 text-[#242F3C] font-semibold ">
              {t('object_storage')}
            </h6>

            <div className="flex flex-col pt-5 pb-4">
              <div className="flex flex-col justify-between pt-5 pb-4 md:pb-10 lg:items-end ">
                {' '}
                <div className="flex items-center">
                  <div className="w-full pb-3 xl:w-96 md:w-52 md:justify-start xl:pb-0 ">
                    <p className="xl:pl-6  xl:text-[16px] xl:font-normal text-[#7B8794] text-sm ">
                      {t('name_bucket')}
                    </p>
                  </div>
                  <div className="w-full">
                    <input
                      {...register(`bucket_name`, {
                        required: true,
                      })}
                      className="w-full p-2 text-base border-[1px] outline-none border-[#d3e8f7] focus:border-primary rounded-lg  placeholder:font-normal placeholder:text-base"
                    />
                    {errors.bucket_name?.message && (
                      <div style={{ color: 'red' }}>
                        {t(errors?.bucket_name?.message)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col">
                  <p
                    className="select-none cursor-pointer mt-1 ml-auto w-max xl:text-[16px] xl:font-normal text-[#7B8794] text-sm pt-1 flex justify-between items-center"
                    onClick={toggleDropdown}
                  >
                    {t('hide_bucket')}
                    <IconDownarrow className="w-4 ml-1" />
                  </p>

                  {isOpen && (
                    <ul className="grid grid-flow-col grid-rows-4 gap-2 pt-6 xl:pl-6">
                      {validations.map((option) => (
                        <li
                          key={option.text}
                          onClick={() => handleOptionClick()}
                          className="text-[11px]"
                        >
                          {option.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="flex flex-col justify-between pt-5 md:flex-row lg:items-center ">
                <div className="w-full pb-3 xl:w-96 md:w-52 xl:pb-0 md:justify-start ">
                  <p className="xl:pl-6 xl:text-[16px] xl:font-normal text-[#7B8794]  text-sm ">
                    {t('storage_size')}
                  </p>
                </div>

                <section className="flex items-center w-full gap-8 md:w-1/2">
                  <label className="w-full md:w-[100px]">
                    <input
                      {...register(`bucket_quota`, {
                        valueAsNumber: true,
                        onChange: (e) => {
                          const inputValue = e.target.value
                          const value = Number(inputValue)
                          if (value <= 0 && inputValue !== '') {
                            return
                          }
                          if (value > 1024) {
                            setValue(1024)
                          } else setValue(inputValue)
                        },
                      })}
                      value={value}
                      inputMode={'numeric'}
                      min={0}
                      type="number"
                      onKeyDown={blockInvalidChar}
                      className="md:w-[100px] w-full p-2 text-base border-[1px] outline-none border-[#d3e8f7] focus:border-primary rounded-lg  placeholder:font-normal placeholder:text-base"
                    />
                  </label>
                  <div>{t('GB')}</div>
                </section>
              </div>
            </div>
          </div>

          {/* SECOND COLUMN */}
          <div className="flex flex-col w-full gap-5 py-5 md:px-6 xl:py-0 ">
            <p className="text-[#242F3C] text-[16px] font-semibold">
              {t('total')}
            </p>
            <div className="flex justify-between">
              <div className="">
                <div className=" flex text-[#7B8794]  text-[16px]">
                  {t('storage_size_per')}
                </div>
                <div className="flex flex-col justify-end font-semibold text-black">
                  {value} {t('GB')}
                </div>
              </div>
              <div className="flex flex-col justify-end  text-[#242F3C] text-[16px] font-semibold ">
                {numberWithSpaces(value * Number(price) || 0)} &nbsp;
                {'UZS'}
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-[#242F3C] text-[20px] font-semibold">
                {value} {t('GB')}
              </div>
              <div className="flex text-[#242F3C] text-[20px] ">
                <div className="font-semibold">
                  {numberWithSpaces(value * Number(price) || 0)}
                </div>{' '}
                <div> &nbsp;</div>
                <div>{t('uzs_month')}</div>
              </div>
            </div>
            <button
              type="submit"
              disabled={!value}
              className="text-center mt-[26px] w-full disabled:bg-slate-500 p-4 bg-[#428BF9] text-white rounded-lg"
            >
              <span className="text-[20px]">{t('to_payment')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
export default Cost
